"use client";

import { useLayoutEffect, useRef, useState } from "react";

/** Middle-ellipsis form of a hex string: `head` + "…" + `tail` characters. */
function squeeze(value: string, keep: number) {
  if (keep * 2 + 1 >= value.length) return value;
  return `${value.slice(0, keep)}…${value.slice(-keep)}`;
}

/**
 * AN ADDRESS, AS LONG AS ITS SPACE ALLOWS.
 *
 * The whole 42 characters wherever they fit; where they do not, the longest
 * `0xabcd…1234` form that does, from the middle out — never a fixed six-and-
 * four cut on a line with room to spare. Measured against the box's own
 * width (a ResizeObserver, so it follows the layout, not a breakpoint), with
 * the element's real font. The full value is always on `title` and stays the
 * accessible text, so what is copied or read aloud is never the short form.
 *
 * The box must be allowed to shrink (`min-inline-size: 0` in a flex/grid
 * track); the look is `.v2-hex`: the display face with tabular figures, the
 * `0x` prefix quiet, isolated LTR so Persian beside it can never reorder it.
 */
export default function FitHex({ value, className = "", min = 4 }: { value: string; className?: string; min?: number }) {
  const ref = useRef<HTMLSpanElement>(null);
  const [shown, setShown] = useState(value);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el || !value) return;
    // Measured with a hidden probe carrying the same classes, inside the same
    // .v2 scope (so the font tokens resolve), outside React's children: the
    // real rendered width in the real font. A canvas cannot resolve this
    // page's font stack; it under-measured and the text overflowed.
    const probe = document.createElement("span");
    probe.className = el.className;
    probe.setAttribute("aria-hidden", "true");
    probe.style.cssText = "position:absolute;visibility:hidden;display:inline-block;inline-size:auto;max-inline-size:none;overflow:visible;white-space:nowrap;top:0;left:0;pointer-events:none";
    (el.closest(".v2") ?? document.body).appendChild(probe);
    const fit = () => {
      const width = (text: string) => { probe.textContent = text; return probe.getBoundingClientRect().width; };
      // A pixel of slack: the quiet 0x is set a weight lighter than the probe.
      const room = el.clientWidth - 1;
      if (room <= 0) return;
      if (width(value) <= room) return setShown(value);
      let lo = min, hi = Math.floor(value.length / 2) - 1, best = min;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (width(squeeze(value, mid)) <= room) { best = mid; lo = mid + 1; } else hi = mid - 1;
      }
      setShown(squeeze(value, best));
    };
    fit();
    const observer = new ResizeObserver(fit);
    observer.observe(el);
    return () => { observer.disconnect(); probe.remove(); };
  }, [value, min]);

  const prefixed = shown.startsWith("0x");
  return (
    <span ref={ref} className={`v2-hex ${className}`} title={value}>
      <span className="v2-sr-only">{value}</span>
      <span aria-hidden>
        {prefixed ? <span className="v2-hex-0x">0x</span> : null}
        {prefixed ? shown.slice(2) : shown}
      </span>
    </span>
  );
}
